import { inject, Injectable } from '@angular/core';
import { ARPagedResponseDataModel } from '@relayter/core';
import { INucTableColumn } from '@relayter/rubber-duck';
import { Observable } from 'rxjs';
import { QueryParams } from '../../classes/query-params';
import { NewCursor } from '../new-api-cursor';
import { ApiDeleteOneRequest } from '../requests/api-delete-one.request';
import { ApiDetailsRequest } from '../requests/api-details.request';
import { ApiPagedRequest } from '../requests/api-paged.request';
import { ApiPatchRequest } from '../requests/api-patch.request';
import { ApiPostRequest } from '../requests/api-post.request';
import { ApiPutRequest } from '../requests/api-put.request';
import { TableSortOptions } from '../table-sort-options';
import { ApiRequestService } from './api-request.service';

@Injectable({
    providedIn: 'root'
})
export abstract class ApiService<T> {
    groups: string[] = [];
    model: new (...args: any[]) => T;
    protected apiRequestService = inject(ApiRequestService);

    protected constructor(groups: string[], model: new (...args: any[]) => T) {
        this.groups = groups;
        this.model = model;
    }

    public findOne(itemId: string): Observable<T> {
        const request = new ApiDetailsRequest(this.groups, this.model, itemId);
        return this.apiRequestService.findOne(request);
    }

    public create(body: Record<string, any>): Observable<T> {
        const request = new ApiPostRequest(this.groups, this.model, body);
        return this.apiRequestService.create<T>(request);
    }

    public deleteOne(itemId: string): Observable<boolean> {
        const request = new ApiDeleteOneRequest(this.groups, itemId);
        return this.apiRequestService.deleteOne(request);
    }

    public find(
        limit?: number,
        offset?: number,
        sortColumns?: TableSortOptions,
        cursor?: NewCursor,
        search?: string,
        filters?: Record<string, any>,
        includeTotals: boolean = false
    ): Observable<ARPagedResponseDataModel<T>> {
        const request = new ApiPagedRequest(this.groups, this.model, limit, offset, sortColumns, cursor, search, filters, includeTotals);
        return this.apiRequestService.find(request);
    }

    public patch(id: string, body: Record<string, any>): Observable<T> {
        const request = new ApiPatchRequest(this.groups, this.model, id, body);
        return this.apiRequestService.patch<T>(request);
    }

    public put(id: string, body: Record<string, any>): Observable<T> {
        const request = new ApiPutRequest(this.groups, this.model, id, body);
        return this.apiRequestService.put<T>(request);
    }

    public findAll(queryParams?: QueryParams, pathComponents: string[] = [], includeTotals = false, sortColumns?: TableSortOptions): Observable<T[]> {
        const request = new ApiPagedRequest([...this.groups, ...pathComponents], this.model);
        request.includeTotals = includeTotals;
        request.tableSortOptions = sortColumns;

        if (queryParams) {
            const params = queryParams.getParams();
            const sortProperties = typeof params['sort'] === 'string' ? params['sort'].split('|').filter(Boolean) : [];
            const sortOrder = params['sortType']?.toLowerCase() === 'desc' ? 'desc' : 'asc';

            request.tableSortOptions = new TableSortOptions(
                sortProperties.map((sortProperty) => ({ sortProperty }) as INucTableColumn),
                sortOrder
            );

            request.additionalQueryParams.addObject(params);
        }

        return this.apiRequestService.findAll(request);
    }
}
